import { useEffect, useId, useState, type FormEvent } from 'react';
import type { DiscountDto, DiscountState, DiscountType } from '@ecom/contracts';
import { EMPTY_DISCOUNT_FILTERS, useDiscountsStore } from '@/stores/discounts.store';
import { useUiStore } from '@/i18n';
import { ToggleField } from '@/components/fields';
import { useT } from '@/i18n/use-t';
import { count, money } from '@/lib/format';
import {
  codeInput,
  discountCreate,
  discountDraft,
  discountUpdate,
  percentLabel,
  type DiscountDraft,
  type DiscountField,
  type DraftError,
} from '@/lib/marketing';
import { PlusIcon, TagIcon } from '@/components/icons';
import { FilterBar } from '@/components/filters';
import { ListCard, ListToolbar, LoadMore, Page, Sheet, Table, TableScroll, Tabs, Td, Th, Tr } from '@/components/page';
import {
  Alert,
  Button,
  ConfirmDialog,
  CopyButton,
  EmptyState,
  Field,
  Input,
  Pill,
  Select,
  TableSkeleton,
  type PillTone,
} from '@/components/ui';
import { useDraftError, useWindowLabel } from './marketing-shared';
import { OfferUsageSheet } from '@/components/offer-usage-sheet';
import { useOfferUsageStore } from '@/stores/offer-usage.store';
import { AsyncSelect } from '@/components/async-select';
import { customerGroupOptions } from '@/lib/options';

/** The derived state is what an operator reads; these are its colours. */
const STATE_TONES: Record<DiscountState, PillTone> = {
  active: 'success',
  scheduled: 'info',
  expired: 'neutral',
  exhausted: 'warning',
  disabled: 'neutral',
};

/**
 * Discount codes.
 *
 * A list that fills the window, searched on the server, with a view for the
 * codes in effect right now. A whole row opens its code in a panel from the
 * far edge — the same panel a new one is written in — so the list stays in
 * view. The state shown is the API's own, derived from the switch, the dates
 * and the usage, so the list and the checkout never disagree about a code.
 */
export function DiscountsPage() {
  const openUsage = useOfferUsageStore((state) => state.open);
  const { items, loading, loadingMore, saving, error, nextCursor, filters, load, loadMore, disable, enable } =
    useDiscountsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const windowLabel = useWindowLabel();

  const [editing, setEditing] = useState<DiscountDto | 'new' | null>(null);
  const [disabling, setDisabling] = useState<DiscountDto | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  const hasFilters = Boolean(filters.search) || filters.activeOnly;

  const newDiscount = (
    <Button onClick={() => setEditing('new')}>
      <PlusIcon className="size-4" />
      {t('newDiscount')}
    </Button>
  );

  return (
    <Page title={t('discounts')} subtitle={t('discountsSubtitle')} actions={newDiscount} fill>
      <ListCard>
        <Tabs
          className="px-2"
          label={t('discounts')}
          value={filters.activeOnly ? 'live' : 'all'}
          onChange={(id) => void load({ activeOnly: id === 'live' })}
          items={[
            { id: 'all', label: t('discountViewAll') },
            { id: 'live', label: t('discountViewLive') },
          ]}
        />
        <ListToolbar>
          <FilterBar
            search={filters.search}
            onSearch={(search) => void load({ search })}
            placeholder={t('searchDiscounts')}
          />
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading ? (
          <TableSkeleton columns={6} />
        ) : items.length === 0 ? (
          hasFilters ? (
            <EmptyState
              title={t('noDiscountsFiltered')}
              icon={<TagIcon />}
              action={
                <Button variant="secondary" onClick={() => void load(EMPTY_DISCOUNT_FILTERS)}>
                  {t('clearFilters')}
                </Button>
              }
            />
          ) : (
            <EmptyState title={t('noDiscounts')} body={t('noDiscountsHint')} icon={<TagIcon />} action={newDiscount} />
          )
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('code')}</Th>
                  <Th>{t('discountType')}</Th>
                  <Th>{t('status')}</Th>
                  <Th>{t('usage')}</Th>
                  <Th>{t('discountDates')}</Th>
                  <Th align="end">
                    <span className="sr-only">{t('moreActions')}</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {items.map((discount) => (
                  <Tr key={discount.id} onClick={() => setEditing(discount)}>
                    <Td>
                      <div className="flex items-center gap-1">
                        {/* A code is typed by a shopper: Latin, left to right. */}
                        <span dir="ltr" data-numeric className="font-medium text-ink">
                          {discount.code}
                        </span>
                        <span onClick={(event) => event.stopPropagation()}>
                          <CopyButton value={discount.code} label={t('copy')} copiedLabel={t('copied')} />
                        </span>
                      </div>
                    </Td>
                    <Td>
                      <span className="block text-ink">
                        {t(`type${discount.type}` as 'typeFIXED')}
                        {discount.type !== 'FREE_SHIPPING' ? (
                          <span data-numeric className="ms-2 font-medium">
                            {discount.type === 'PERCENTAGE'
                              ? percentLabel(discount.value, locale)
                              : money(discount.value, locale)}
                          </span>
                        ) : null}
                      </span>
                      {discount.minOrderTotal ? (
                        <span className="block text-xs text-ink-muted">
                          {t('discountMinOrder', { amount: money(discount.minOrderTotal, locale) })}
                        </span>
                      ) : null}
                    </Td>
                    <Td>
                      <Pill tone={STATE_TONES[discount.state]}>{t(`state${discount.state}` as 'stateactive')}</Pill>
                    </Td>
                    <Td data-numeric>
                      <span className="block text-ink">
                        {discount.maxUses === null
                          ? t('discountUsedNoLimit', { used: count(discount.usedCount, locale) })
                          : t('discountUsedOf', {
                              used: count(discount.usedCount, locale),
                              max: count(discount.maxUses, locale),
                            })}
                      </span>
                      {discount.maxUsesPerUser !== null ? (
                        <span className="block text-xs text-ink-muted">
                          {t('discountPerCustomer', { count: count(discount.maxUsesPerUser, locale) })}
                        </span>
                      ) : null}
                      {discount.newCustomersOnly ? <span className="block text-xs text-ink-muted">{t('discountFirstOrderOnly')}</span> : null}
                    </Td>
                    <Td className="whitespace-nowrap text-ink-muted">
                      {windowLabel(discount.startsAt, discount.endsAt)}
                    </Td>
                    <Td align="end">
                      <div className="flex justify-end gap-1">
                        {/* What the code did: who used it, on which orders, sold or abandoned (ADR-063). */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            void openUsage({ kind: 'coupon', id: discount.id, name: discount.code });
                          }}
                        >
                          {t('usageButton')}
                        </Button>
                        {discount.isActive ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setDisabling(discount);
                            }}
                          >
                            {t('disable')}
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={saving}
                            onClick={(event) => {
                              event.stopPropagation();
                              void enable(discount.id);
                            }}
                          >
                            {t('discountSwitchOn')}
                          </Button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
      </ListCard>

      {editing ? (
        <DiscountSheet
          // One draft per code: opening another starts from its own values.
          key={editing === 'new' ? 'new' : editing.id}
          discount={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <OfferUsageSheet />

      <ConfirmDialog
        open={disabling !== null}
        title={t('disableTitle')}
        body={t('disableBody')}
        confirmLabel={t('disable')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (disabling) await disable(disabling.id);
          setDisabling(null);
        }}
        onCancel={() => setDisabling(null)}
      />
    </Page>
  );
}

/**
 * Writing a new code, or changing one. The code itself and its type are fixed
 * once created — the code may already be printed — so an edit shows them and
 * changes the rest. An edit sends only what changed.
 */
function DiscountSheet({ discount, onClose }: { discount: DiscountDto | null; onClose: () => void }) {
  const { create, update, saving, saveError, clearSaveError } = useDiscountsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const errorText = useDraftError();
  const formId = useId();

  const [draft, setDraft] = useState<DiscountDraft>(() => discountDraft(discount));
  const [errors, setErrors] = useState<Partial<Record<DiscountField, DraftError>>>({});

  // A refusal from the last panel is not this one's.
  useEffect(() => {
    clearSaveError();
  }, [clearSaveError]);

  const set = (patch: Partial<DiscountDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      for (const key of Object.keys(patch) as DiscountField[]) delete next[key];
      return next;
    });
  };

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    // A value that cannot be read is said beside its field — never a save that does nothing.
    if (discount) {
      const result = discountUpdate(draft, discount);
      if (!result.ok) {
        setErrors(result.errors);
      } else if (Object.keys(result.body).length === 0 || (await update(discount.id, result.body))) {
        // Nothing changed is nothing to send.
        onClose();
      }
      return;
    }

    const result = discountCreate(draft);
    if (!result.ok) setErrors(result.errors);
    else if (await create(result.body)) onClose();
  }

  const invalid = Object.keys(errors).length > 0;

  return (
    <Sheet
      open
      title={discount ? t('editDiscount') : t('newDiscount')}
      subtitle={t('codeImmutable')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button type="submit" form={formId} disabled={saving}>
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {discount ? (
          // What an edit cannot change, shown rather than offered.
          <dl className="grid grid-cols-2 gap-3 rounded-control border border-line bg-surface-sunken p-3 text-sm">
            <div className="flex flex-col gap-0.5">
              <dt className="text-xs text-ink-muted">{t('code')}</dt>
              <dd className="flex items-center gap-1">
                <span dir="ltr" data-numeric className="font-medium text-ink">
                  {discount.code}
                </span>
                <CopyButton value={discount.code} label={t('copy')} copiedLabel={t('copied')} />
              </dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-xs text-ink-muted">{t('discountType')}</dt>
              <dd className="text-ink">{t(`type${discount.type}` as 'typeFIXED')}</dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-xs text-ink-muted">{t('status')}</dt>
              <dd>
                <Pill tone={STATE_TONES[discount.state]}>{t(`state${discount.state}` as 'stateactive')}</Pill>
              </dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-xs text-ink-muted">{t('discountUsedSoFar')}</dt>
              <dd data-numeric className="text-ink">
                {count(discount.usedCount, locale)}
              </dd>
            </div>
          </dl>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          {discount ? null : (
            <>
              <Field label={t('code')} htmlFor="discount-code" error={errorText(errors.code)}>
                <Input
                  id="discount-code"
                  dir="ltr"
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  value={draft.code}
                  invalid={Boolean(errors.code)}
                  onChange={(event) => set({ code: codeInput(event.target.value) })}
                />
              </Field>

              <Field label={t('discountType')} htmlFor="discount-type">
                <Select
                  id="discount-type"
                  value={draft.type}
                  onChange={(event) => set({ type: event.target.value as DiscountType, value: '' })}
                  className="w-full"
                >
                  <option value="PERCENTAGE">{t('typePERCENTAGE')}</option>
                  <option value="FIXED">{t('typeFIXED')}</option>
                  <option value="FREE_SHIPPING">{t('typeFREE_SHIPPING')}</option>
                </Select>
              </Field>
            </>
          )}

          {/* Free shipping carries no amount — the field would only confuse. */}
          {draft.type !== 'FREE_SHIPPING' ? (
            <Field
              label={t('value')}
              htmlFor="discount-value"
              hint={draft.type === 'PERCENTAGE' ? t('valuePercentHint') : t('valueFixedHint')}
              error={errorText(errors.value)}
            >
              <Input
                id="discount-value"
                dir="ltr"
                inputMode={draft.type === 'PERCENTAGE' ? 'numeric' : 'decimal'}
                value={draft.value}
                invalid={Boolean(errors.value)}
                onChange={(event) => set({ value: event.target.value })}
              />
            </Field>
          ) : null}

          <Field
            label={t('minOrderTotal')}
            htmlFor="discount-minimum"
            hint={t('discountMinOrderHint')}
            error={errorText(errors.minOrderTotal)}
          >
            <Input
              id="discount-minimum"
              dir="ltr"
              inputMode="decimal"
              value={draft.minOrderTotal}
              invalid={Boolean(errors.minOrderTotal)}
              onChange={(event) => set({ minOrderTotal: event.target.value })}
            />
          </Field>

          <Field label={t('maxUses')} htmlFor="discount-max" hint={t('maxUsesHint')} error={errorText(errors.maxUses)}>
            <Input
              id="discount-max"
              dir="ltr"
              inputMode="numeric"
              value={draft.maxUses}
              invalid={Boolean(errors.maxUses)}
              onChange={(event) => set({ maxUses: event.target.value })}
            />
          </Field>

          <Field
            label={t('maxUsesPerUser')}
            htmlFor="discount-per-customer"
            hint={t('discountPerCustomerHint')}
            error={errorText(errors.maxUsesPerUser)}
          >
            <Input
              id="discount-per-customer"
              dir="ltr"
              inputMode="numeric"
              value={draft.maxUsesPerUser}
              invalid={Boolean(errors.maxUsesPerUser)}
              onChange={(event) => set({ maxUsesPerUser: event.target.value })}
            />
          </Field>

          {/* First orders only (ADR-114): no sold order before, by account or email. */}
          <ToggleField
            label={t('discountFirstOrderOnly')}
            hint={t('discountFirstOrderOnlyHint')}
            checked={draft.newCustomersOnly}
            onChange={(newCustomersOnly) => set({ newCustomersOnly })}
          />

          {/* For one customer group's members (ADR-085). */}
          <Field label={t('customerGroup')} htmlFor="discount-group" hint={t('discountGroupHint')}>
            <AsyncSelect
              id="discount-group"
              value={draft.customerGroupId}
              emptyLabel={t('discountAnyCustomer')}
              fetchPage={customerGroupOptions.page}
              fetchOne={customerGroupOptions.one}
              onChange={(id) => set({ customerGroupId: id })}
            />
          </Field>

          <Field
            label={t('startsAt')}
            htmlFor="discount-starts"
            hint={t('discountStartsHint')}
            error={errorText(errors.startsAt)}
          >
            <Input
              id="discount-starts"
              type="date"
              dir="ltr"
              value={draft.startsAt}
              invalid={Boolean(errors.startsAt)}
              onChange={(event) => set({ startsAt: event.target.value })}
            />
          </Field>

          <Field label={t('endsAt')} htmlFor="discount-ends" hint={t('discountEndsHint')} error={errorText(errors.endsAt)}>
            <Input
              id="discount-ends"
              type="date"
              dir="ltr"
              value={draft.endsAt}
              invalid={Boolean(errors.endsAt)}
              onChange={(event) => set({ endsAt: event.target.value })}
            />
          </Field>
        </div>

        {invalid ? <Alert>{t('mktCheckFields')}</Alert> : null}
        {saveError ? <Alert>{saveError}</Alert> : null}
      </form>
    </Sheet>
  );
}
